import type {CollectionField} from '@packages/systems/dynamo/types';

export const dynamicContextConstant = 'DYN_CONTEXT';

// Lowercase field names that are reserved/denied
export const FIELD_NAME_DENYLIST: Array<string> = ['_id', 'id', '_fileRefs'];

// Managed Plan limits:
export const MAX_MANAGED_PLAN_AMOUNT = 20000000; // in cents

/**
 * Default fields - should be in the format in which they are stored in the Collection model.
 * @type {Array}
 */
export const dynamoCollectionDefaultFields: Array<CollectionField> = [
  {
    name: 'Name',
    slug: 'name',
    type: 'PlainText',
    required: true,
    editable: true,
    validations: {
      maxLength: 256,
    },
  },
  {
    name: 'Slug',
    slug: 'slug',
    type: 'PlainText',
    required: true,
    editable: true,
    unique: true,
    validations: {
      maxLength: 256,
      pattern: /^[_a-zA-Z0-9][-_a-zA-Z0-9]*$/,
      messages: {
        pattern:
          'Must be alphanumerical and not contain any spaces or special characters',
        maxLength: 'Must be less than 256 characters',
      },
    },
  },
  {
    name: 'Archived',
    slug: '_archived',
    type: 'Bool',
    required: true,
    editable: true,
    default: false,
  },
  {
    name: 'Draft',
    slug: '_draft',
    type: 'Bool',
    required: true,
    editable: true,
    default: false,
  },
  {
    name: 'Created On',
    slug: 'created-on',
    type: 'Date',
    required: false,
    editable: false,
  },
  {
    name: 'Updated On',
    slug: 'updated-on',
    type: 'Date',
    required: false,
    editable: false,
  },
  {
    name: 'Published On',
    slug: 'published-on',
    type: 'Date',
    required: false,
    editable: false,
  },
  {
    name: 'Created By',
    slug: 'created-by',
    type: 'User',
    required: false,
    editable: false,
  },
  {
    name: 'Updated By',
    slug: 'updated-by',
    type: 'User',
    required: false,
    editable: false,
  },
  {
    name: 'Published By',
    slug: 'published-by',
    type: 'User',
    required: false,
    editable: false,
  },
  // _noSearch, when set to true, is used to prevent item from
  // being discoverable by external sources:
  // - omit the item's page from the automated sitemaps
  // - add no-index to item's page
  {
    name: 'No Search',
    slug: '_noSearch',
    type: 'Bool',
    required: false,
    editable: true,
    default: false,
  },
];

export const dynamoCollectionDefaultFieldSlugs: Array<string> =
  dynamoCollectionDefaultFields.map(function (field) {
    return field.slug;
  });

type ReservedFieldSlugRegexPattern = {
  type: 'is' | 'prefix';
  value: RegExp;
};

export const RESERVED_CUSTOM_FIELD_SLUG_REGEX_PATTERNS: Array<ReservedFieldSlugRegexPattern> =
  [
    {type: 'prefix', value: /^ec-product-/},
    {type: 'prefix', value: /^ec-sku-/},
    {type: 'prefix', value: /^ec-category-/},
    // Prevent collisions with collection item fullSlug GraphQL field
    {type: 'is', value: /^full-slug$/},
    {type: 'is', value: /^fullslug$/},
  ];

export const frontEndReservedFieldSlugPatterns: Array<ReservedFieldSlugRegexPattern> =
  []
    .concat(
      // @ts-expect-error - TS2769 - No overload matches this call.
      dynamoCollectionDefaultFieldSlugs.map(function (slug) {
        return {
          type: 'is',
          value: new RegExp('^' + slug + '$'),
        };
      })
    )
    .concat(
      // @ts-expect-error - TS2769 - No overload matches this call.
      FIELD_NAME_DENYLIST.map(function (fieldName) {
        return {
          type: 'is',
          value: new RegExp('^' + fieldName + '$', 'i'),
        };
      })
    )
    // @ts-expect-error - TS2769 - No overload matches this call.
    .concat(RESERVED_CUSTOM_FIELD_SLUG_REGEX_PATTERNS);

export const frontEndReservedFieldNames: Array<string> =
  dynamoCollectionDefaultFields.map(function (field) {
    return field.name;
  });

type EnumOptionMap = {
  code: string;
  name: string;
};
type EnumOptionSets<SetType> = {
  TaxCategories: SetType[];
  BillingMethods: SetType[];
  BillingPeriods: SetType[];
};

export const enumOptionValueSets: EnumOptionSets<string> = {
  TaxCategories: [
    'books',
    'books-religious',
    'books-textbook',
    'clothing',
    'clothing-swimwear',
    'digital-goods',
    'digital-service',
    'drugs-non-prescription',
    'drugs-prescription',
    'food-bottled-water',
    'food-candy',
    'food-groceries',
    'food-prepared',
    'food-soda',
    'food-supplements',
    'magazine-individual',
    'magazine-subscription',
    'service-admission',
    'service-advertising',
    'service-dry-cleaning',
    'service-hairdressing',
    'service-installation',
    'service-miscellaneous',
    'service-parking',
    'service-printing',
    'service-professional',
    'service-repair',
    'service-training',
    'standard-exempt',
    'standard-taxable',
    'food-beverage-wine',
  ],
  BillingMethods: ['one-time', 'subscription'],
  BillingPeriods: ['day', 'week', 'month', 'year'],
};

export const enumOptionMapSets: EnumOptionSets<EnumOptionMap> = {
  TaxCategories: [
    {code: 'standard-taxable', name: 'Standard automatic tax calculation'},
    {code: 'standard-exempt', name: 'Exempt from taxes'},
    {code: 'books', name: 'Books'},
    {code: 'books-religious', name: 'Books - Religious'},
    {code: 'books-textbook', name: 'Books - Textbooks'},
    {code: 'clothing', name: 'Clothing'},
    {code: 'clothing-swimwear', name: 'Clothing - Swimwear'},
    {code: 'digital-goods', name: 'Digital Goods'},
    {code: 'digital-service', name: 'Digital Services'},
    {code: 'drugs-non-prescription', name: 'Drugs - Non-Prescription'},
    {code: 'drugs-prescription', name: 'Drugs - Prescription'},
    {code: 'food-bottled-water', name: 'Food - Bottled Water'},
    {code: 'food-candy', name: 'Food - Candy'},
    {code: 'food-groceries', name: 'Food - Groceries'},
    {code: 'food-prepared', name: 'Food - Prepared'},
    {code: 'food-soda', name: 'Food - Soda'},
    {code: 'food-supplements', name: 'Food - Supplements'},
    {code: 'magazine-individual', name: 'Magazine - Individual'},
    {code: 'magazine-subscription', name: 'Magazine - Subscription'},
    {code: 'service-admission', name: 'Service - Admission'},
    {code: 'service-advertising', name: 'Service - Advertising'},
    {code: 'service-dry-cleaning', name: 'Service - Dry Cleaning'},
    {code: 'service-hairdressing', name: 'Service - Hairdressing'},
    {code: 'service-installation', name: 'Service - Installation'},
    {code: 'service-miscellaneous', name: 'Service - Miscellaneous'},
    {code: 'service-parking', name: 'Service - Parking'},
    {code: 'service-printing', name: 'Service - Printing'},
    {code: 'service-professional', name: 'Service - Professional'},
    {code: 'service-repair', name: 'Service - Repair'},
    {code: 'service-training', name: 'Service - Training'},
    {
      code: 'food-beverage-wine',
      name: 'Food and Beverage - Alcoholic beverages - Wine',
    },
  ],
  BillingMethods: [
    {code: 'one-time', name: 'One-time payment'},
    {code: 'subscription', name: 'Subscription'},
  ],
  BillingPeriods: [
    {code: 'day', name: 'Day'},
    {code: 'week', name: 'Week'},
    {code: 'month', name: 'Month'},
    {code: 'year', name: 'Year'},
  ],
};

export const dynamoPageSlugPrefix = 'detail_';

type CollectionPreset = {
  id: string;
  name: string;
  properties: {
    editable: boolean;
    name: string;
    slug: string;
    fields: Array<{
      name: string;
      slug?: string;
      type: string;
      required: boolean;
      validations?: {
        minLength: number;
        maxLength?: number;
      };
    }>;
  };
};

export const collectionPresets: Array<CollectionPreset> = [
  {
    id: 'blog-post', // The id field is not important, any random field that doesn't clash with other ids defined here is good.
    name: 'Blog Post', // Name in drop down
    properties: {
      editable: true, // Editable by contributors
      name: 'Blog Post',
      slug: 'blog-post',
      fields: [
        {
          name: 'Blog Post Body',
          type: 'RichText',
          required: true,
          validations: {
            minLength: 20,
          },
        },
        {
          name: 'Thumbnail Image',
          type: 'ImageRef',
          required: false,
        },
        {
          name: 'Main Image',
          type: 'ImageRef',
          required: false,
        },
        {
          name: 'Category',
          slug: 'category',
          type: 'PlainText',
          required: false,
          validations: {
            minLength: 1,
            maxLength: 100,
          },
        },
      ],
    },
  },
];

/**
    These field types are the ones visible in the Data Manager list view.
   */
export const listableFieldTypes: Array<string> = [
  'Date',
  'PlainText',
  'Bool',
  'Color',
  'ItemRef',
  'Option',
  'Number',
  'Link',
  'Email',
  'Phone',
  'CommercePrice',
  'FileRef',
  'ID',
  'Status',
];

/**
    A "direction" sort is defined by being useful to the user, and one that we can perform on the backend, such as:
      - Alphabetical (Plaintext)
      - Ascending/descending (Number)
      - Earliest/latest (Date)
      - True/False first (Bool)
   */
export const sortableByDirectionFieldTypes: Array<string> = [
  'PlainText',
  'Number',
  'Date',
  'Bool',
];

/**
    An "existence" sort is defined by whether or not the field has a value

    This array must be a subset of listableFieldTypes
  */
export const sortableByExistenceFieldTypes: Array<string> = [
  'Color',
  'Email',
  'FileRef',
  'ItemRef',
  'Link',
  'Option',
  'Phone',
];

export const expandableFieldTypes: Array<string> = [
  'ItemRef',
  'ItemRefSet',
  'Option',
];

export const heavyExpandableFieldTypes: Array<string> = [
  'ItemRef',
  'ItemRefSet',
];
